import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event.
   *
   * Pen/Vial variant images for the SAME product are preloaded into the
   * browser cache on `connectedCallback` (see #preloadVariantImages). On
   * variant change we just update the `src`/`srcset` on the EXISTING <img>
   * element instead of replacing the whole <media-gallery>. No fresh element,
   * no missing-image flash, no layout reflow — the swap is effectively instant.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const newImg = newMediaGallery.querySelector('.product-media__image2, .product-media img');
    const oldImg = this.querySelector('.product-media__image2, .product-media img');
    if (newImg && oldImg && newImg.getAttribute('src')) {
      const newSrc = newImg.getAttribute('src');
      const newSrcset = newImg.getAttribute('srcset');
      if (oldImg.getAttribute('src') !== newSrc) {
        if (newSrcset) oldImg.setAttribute('srcset', newSrcset);
        else oldImg.removeAttribute('srcset');
        oldImg.setAttribute('src', newSrc);
        const lb = newImg.getAttribute('data-peptik-lightbox');
        if (lb) oldImg.setAttribute('data-peptik-lightbox', lb);
        const alt = newImg.getAttribute('alt');
        if (alt) oldImg.setAttribute('alt', alt);
      }
      return;
    }
    // Fallback: replace whole element (e.g. when image markup structure changes).
    this.replaceWith(newMediaGallery);
  };


  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
